// src/components/editions/EditionsDrawer.tsx
//
// A report's editions (plan 2026-10-23-an-edition-is-an-argument-keyed-
// snapshot, C6), in the shape of the version history drawer: newest first,
// each row with its window, status, Preview badge, version, who and when,
// and **Load more** at the bottom. A row opens the edition page. While an
// edition is being built the list polls until it settles, so a build started
// from the button shows up here and turns Ready (or Partly built) by itself.
//
// WHO SEES WHAT. The server lists a preview only to a caller who may edit
// the report; the drawer filters nothing itself.
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Group, Paper, Skeleton, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconStack2 } from "@tabler/icons-react";

import {
  EDITION_PAGE_SIZE,
  editionWindow,
  isEditionInFlight,
  listEditions,
  type EditionStatus,
  type EditionSummary,
} from "@/api/editions";
import { AppDrawer } from "@/components/drawers/AppDrawer";
import { dayLabel } from "@/components/dashboards/versions/VersionHistoryDrawer";
import {
  EDITION_COPY,
  EDITION_STATUS_COLOR,
  EDITION_STATUS_LABEL,
  partialSummary,
  versionLabel,
} from "./editionCopy";

/** How often the list is re-read while an edition is in flight. */
export const EDITION_POLL_MS = 3000;

export type EditionsDrawerProps = {
  opened: boolean;
  onClose: () => void;
  dashboardId: string;
  /** Open one edition (the page route). */
  onOpenEdition: (editionPublicId: string) => void;
  /** Bumped by the caller after it started a build, so an open drawer
   *  re-reads its first page. */
  reloadToken?: number;
};

export default function EditionsDrawer(props: EditionsDrawerProps) {
  return (
    <AppDrawer
      opened={props.opened}
      onClose={props.onClose}
      title={EDITION_COPY.drawerTitle}
      leading={<IconStack2 size={22} />}
      size="narrow"
    >
      {/* Mounted only while open, like the version history: every opening
          starts from a fresh first page. */}
      {props.opened && <EditionsBody {...props} />}
    </AppDrawer>
  );
}

function formatRange(start: string, end: string): string {
  const fmt = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
}

function timeLabel(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function EditionsBody({ dashboardId, onOpenEdition, reloadToken }: EditionsDrawerProps) {
  const [editions, setEditions] = useState<EditionSummary[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // A generation per first-page load, so a slow answer never lands on top of
  // a newer one (a retry, a poll, a reload after a build).
  const generation = useRef(0);

  const loadFirst = useCallback(
    async (quiet: boolean) => {
      const gen = ++generation.current;
      if (!quiet) {
        setLoading(true);
        setLoadError(false);
      }
      try {
        const list = await listEditions(dashboardId, { page: 1, limit: EDITION_PAGE_SIZE });
        if (gen !== generation.current) return;
        setEditions(list.editions);
        setPage(1);
        setHasMore(list.has_more);
        setLoadError(false);
      } catch {
        if (gen !== generation.current) return;
        // A failed quiet poll keeps the list it has; the next poll retries.
        if (!quiet) setLoadError(true);
      } finally {
        if (gen === generation.current && !quiet) setLoading(false);
      }
    },
    [dashboardId]
  );

  useEffect(() => {
    void loadFirst(false);
  }, [loadFirst, reloadToken]);

  const inFlight = editions.some((e) => isEditionInFlight(e.status));
  useEffect(() => {
    if (!inFlight) return;
    const timer = setTimeout(() => void loadFirst(true), EDITION_POLL_MS);
    return () => clearTimeout(timer);
  }, [inFlight, editions, loadFirst]);

  const loadMore = async () => {
    const gen = generation.current;
    setLoadingMore(true);
    try {
      const next = await listEditions(dashboardId, { page: page + 1, limit: EDITION_PAGE_SIZE });
      if (gen !== generation.current) return;
      setEditions((prev) => [...prev, ...next.editions.filter((e) => !prev.some((p) => p.public_id === e.public_id))]);
      setPage(page + 1);
      setHasMore(next.has_more);
    } catch {
      if (gen === generation.current) setLoadError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <Stack gap="xs" data-testid="editions-loading">
        <Skeleton height={56} />
        <Skeleton height={56} />
        <Skeleton height={56} />
      </Stack>
    );
  }
  if (loadError && editions.length === 0) {
    return (
      <Alert color="red" variant="light" title={EDITION_COPY.loadError}>
        <Button size="xs" variant="light" onClick={() => void loadFirst(false)}>
          {EDITION_COPY.tryAgain}
        </Button>
      </Alert>
    );
  }
  if (editions.length === 0) {
    return (
      <Text size="sm" c="dimmed" data-testid="editions-empty">
        {EDITION_COPY.empty}
      </Text>
    );
  }
  return (
    <Stack gap="xs">
      {editions.map((edition) => (
        <EditionRow key={edition.public_id} edition={edition} onOpen={() => onOpenEdition(edition.public_id)} />
      ))}
      {hasMore && (
        <Button variant="subtle" size="xs" loading={loadingMore} onClick={() => void loadMore()}>
          {EDITION_COPY.loadMore}
        </Button>
      )}
    </Stack>
  );
}

function EditionRow({ edition, onOpen }: { edition: EditionSummary; onOpen: () => void }) {
  const status = edition.status as EditionStatus;
  const win = editionWindow(edition);
  const building = isEditionInFlight(edition.status);
  const meta = [
    versionLabel(edition.version_number),
    edition.run_by_name,
    `${dayLabel(edition.created_at)} ${timeLabel(edition.created_at)}`.trim(),
  ].filter(Boolean);
  const body = (
    <Paper withBorder radius="sm" p="sm">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="sm" fw={500} truncate>
            {win ? win.label || formatRange(win.start, win.end) : EDITION_STATUS_LABEL[status]}
          </Text>
          {win && win.label && (
            <Text size="xs" c="dimmed">
              {formatRange(win.start, win.end)}
            </Text>
          )}
          <Text size="xs" c="dimmed">
            {meta.join(" · ")}
          </Text>
          {status === "PARTIAL" && (
            <Text size="xs" c="yellow.8">
              {partialSummary(edition.counts)}
            </Text>
          )}
        </Stack>
        <Group gap={4} wrap="nowrap">
          {edition.preview && (
            <Badge size="sm" variant="outline" color="gray">
              {EDITION_COPY.preview}
            </Badge>
          )}
          <Badge size="sm" variant="light" color={EDITION_STATUS_COLOR[status] ?? "gray"}>
            {EDITION_STATUS_LABEL[status] ?? edition.status}
          </Badge>
        </Group>
      </Group>
    </Paper>
  );
  // A building or failed edition has nothing to read yet.
  if (building || status === "FAILED") return <div data-testid={`edition-row-${edition.public_id}`}>{body}</div>;
  return (
    <UnstyledButton onClick={onOpen} data-testid={`edition-row-${edition.public_id}`} aria-label={`Open edition ${win?.label ?? ""}`.trim()}>
      {body}
    </UnstyledButton>
  );
}
